import axios from "../utils/axios";

/**
 * AssetService - Comprehensive service for asset management
 * Handles assets, categories, items, baselines, compliance, and reporting
 * Includes intelligent caching for performance optimization
 * 
 * @version 2.0
 * @author IMS Development Team
 */
class AssetService {
    // ==================== CACHE MANAGEMENT ====================
    
    // Cache for frequently accessed data
    cache = new Map();
    
    // Cache timeout in milliseconds (5 minutes)
    CACHE_TIMEOUT = 5 * 60 * 1000;
    
    /**
     * Get cached data if still valid
     */
    getCachedData(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.CACHE_TIMEOUT) {
            return cached.data;
        }
        return null;
    }
    
    /**
     * Store data in cache with timestamp
     */
    setCachedData(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }
    
    /**
     * Clear cache by pattern or clear all
     */
    clearCache(keyPattern = null) {
        if (keyPattern) {
            for (let key of this.cache.keys()) {
                if (key.includes(keyPattern)) {
                    this.cache.delete(key);
                }
            }
        } else {
            this.cache.clear();
        }
    }

    /**
     * Refresh all cached data
     */
    refreshCache() {
        this.cache.clear();
        console.log('Asset service cache cleared');
    }

    /**
     * Get cache statistics
     */
    getCacheStats() {
        const stats = {
            totalKeys: this.cache.size,
            keys: Array.from(this.cache.keys()),
            oldestEntry: null,
            newestEntry: null
        };

        if (this.cache.size > 0) {
            const timestamps = Array.from(this.cache.values()).map(v => v.timestamp);
            stats.oldestEntry = new Date(Math.min(...timestamps));
            stats.newestEntry = new Date(Math.max(...timestamps));
        }

        return stats;
    }

    /**
     * Preload commonly accessed data
     */
    async preloadCommonData() {
        try {
            console.log('Preloading common asset data...');
            
            await Promise.all([
                this.allItems({ status: 1 }),
                this.allFunctionalStatus(),
                this.getComplianceAnalytics()
            ]);
            
            console.log('Common data preloaded successfully');
        } catch (error) {
            console.warn('Failed to preload some common data:', error);
        }
    }

    // ==================== ASSET CATEGORIES ====================
    
    /**
     * Get paginated asset categories
     */
    cats(params) {
        return axios.get("assetCats", { params });
    }
    
    /**
     * Get all asset categories
     */
    allCats(params) {
        return axios.get("assetCats/ALL", { params });
    }
    
    /**
     * Save or update asset category
     */
    saveCat(data) {
        this.clearCache('cats');
        return axios.post("assetCatSave", data);
    }
    
    /**
     * Delete asset category
     */
    deleteCat(id) {
        this.clearCache('cats');
        return axios.post("assetCatDelete", { id });
    }

    // ==================== ASSET SUBCATEGORIES ====================
    
    /**
     * Get paginated asset subcategories
     */
    subCats(params) {
        return axios.get("assetSubCats", { params });
    }
    
    /**
     * Get all asset subcategories
     */
    allSubCats(params) {
        return axios.get("assetSubCats/ALL", { params });
    }
    
    /**
     * Save or update asset subcategory
     */
    saveSubCat(data) {
        this.clearCache('subcats');
        return axios.post("assetSubCatSave", data);
    }
    
    /**
     * Delete asset subcategory
     */
    deleteSubCat(id) {
        this.clearCache('subcats');
        return axios.post("assetSubCatDelete", { id });
    }
    
    /**
     * Get infrastructure subcategories
     */
    infrastuctureSubCats(params) {
        return axios.get("infrastuctureSubCats", { params });
    }

    // ==================== ITEMS MANAGEMENT ====================
    
    /**
     * Get paginated items
     */
    items(params) {
        return axios.get("items", { params });
    }
    
    /**
     * Get all items
     */
    allItems(params) {
        return axios.get("items/ALL", { params });
    }
    
    /**
     * Save or update item
     */
    saveItem(data) {
        this.clearCache('items');
        return axios.post("saveItem", data);
    }
    
    /**
     * Delete item
     */
    deleteItem(id) {
        this.clearCache('items');
        return axios.post("deleteItem", { id });
    }

    /**
     * Bulk import items from Excel/CSV
     */
    bulkImportItems(data) {
        this.clearCache('items');
        return axios.post("bulkImportItems", { items: data });
    }

    // ==================== UNITS OF MEASURE ====================
    
    /**
     * Get paginated UOMs
     */
    uoms(params) {
        return axios.get("uoms", { params });
    }
    
    /**
     * Get all UOMs
     */
    allUom(params) {
        return axios.get("uoms/ALL", { params });
    }
    
    /**
     * Save or update UOM
     */
    saveUom(data) {
        this.clearCache('uoms');
        return axios.post("saveUom", data);
    }
    
    /**
     * Delete UOM
     */
    deleteUom(id) {
        this.clearCache('uoms');
        return axios.post("deleteUom", { id });
    }

    // ==================== MODELS MANAGEMENT ====================
    
    /**
     * Get paginated models
     */
    models(params) {
        return axios.get("models", { params });
    }
    
    /**
     * Get all models
     */
    allModel(params) {
        return axios.get("models/ALL", { params });
    }
    
    /**
     * Save or update model
     */
    saveModel(data) {
        this.clearCache('models');
        return axios.post("saveModel", data);
    }
    
    /**
     * Delete model
     */
    deleteModel(id) {
        this.clearCache('models');
        return axios.post("deleteModel", { id });
    }

    // ==================== REFERENCE DATA ====================
    
    /**
     * Get all functional statuses
     */
    allFunctionalStatus(params) {
        const cacheKey = 'functional_status';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return Promise.resolve({ data: cached });
        }

        return axios.get("allFunctionalStatus", { params }).then(response => {
            this.setCachedData(cacheKey, response.data);
            return response;
        });
    }
    
    /**
     * Get all suppliers
     */
    allSuppliers(params) {
        return axios.get("allSuppliers", { params });
    }

    // ==================== ASSETS CRUD ====================
    
    /**
     * Get paginated assets list
     */
    lists(params) {
        return axios.get("assets", { params });
    }
    
    /**
     * Get paginated assets list (alternate route)
     */
    listsR(params) {
        return axios.get("assetsR", { params });
    }
    
    /**
     * Get paginated assets list (version 1)
     */
    lists1(params) {
        return axios.get("assets1", { params });
    }

    /**
     * Get all assets
     */
    all(params) {
        return axios.get("assets/ALL", { params });
    }
    
    /**
     * Delete asset
     */
    delete(id) {
        this.clearCache('compliance');
        this.clearCache('baseline');
        this.clearCache('assets');
        return axios.post("deleteAsset", { id });
    }

    /**
     * Get assets by station ID
     */
    getAssetsByStationId(params) {
        return axios.post("getAssetsByStationId", params); 
    }
    
    /**
     * Save or update asset with photo support
     */
    save(data) {
        this.clearCache('compliance');
        this.clearCache('baseline');
        this.clearCache('assets_without_baselines');
        
        const saveData = { ...data };
        
        // Handle station_point_ids conversion
        if (saveData.station_point_ids) {
            if (Array.isArray(saveData.station_point_ids)) {
                saveData.station_point_ids = saveData.station_point_ids.length > 0 
                    ? saveData.station_point_ids[0] 
                    : null;
            }
            if (saveData.station_point_ids) {
                saveData.station_point_ids = parseInt(saveData.station_point_ids);
            }
        }
        
        // Ensure eibatchnumber is included for supplies (asset_cat_id = '9')
        if (data.eibatchnumber !== undefined) {
            saveData.eibatchnumber = data.eibatchnumber;
        }
        
        // Handle photo uploads with multipart/form-data
        if (data.photos && data.photos.length > 0) {
            const formData = new FormData();
            
            // Add all fields to FormData
            Object.keys(saveData).forEach(key => {
                if (key !== 'photos') {
                    if (key === 'station_point_ids') {
                        if (saveData[key] !== null && saveData[key] !== undefined) {
                            formData.append(key, saveData[key]);
                        }
                    }
                    else if (Array.isArray(saveData[key])) {
                        saveData[key].forEach((item, index) => {
                            formData.append(`${key}[${index}]`, item);
                        });
                    }
                    else if (saveData[key] !== null && saveData[key] !== undefined) {
                        formData.append(key, saveData[key]);
                    }
                }
            });
            
            // Add photo files
            data.photos.forEach((photo, index) => {
                if (photo.file && photo.isNew) {
                    formData.append(`photos[${index}]`, photo.file, photo.name);
                }
            });
            
            return axios.post("saveAsset", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } else {
            return axios.post("saveAsset", saveData);
        }
    }

    /**
     * Transfer asset to another facility
     */
    transferAsset(data) {
        this.clearCache('compliance');
        this.clearCache('assets');
        return axios.post("transferAsset", { ...data });
    }

    // ==================== PHOTO MANAGEMENT ====================
    
    /**
     * Get photos for an asset
     */
    getAssetPhotos(assetId) {
        return axios.get(`assetPhotos/${assetId}`);
    }

    /**
     * Delete an asset photo
     */
    deleteAssetPhoto(photoId) {
        return axios.post("deleteAssetPhoto", { id: photoId });
    }

    // ==================== DUPLICATE DETECTION ====================
    
    /**
     * Advanced duplicate detection with backend support
     */
    async checkDuplicates(assetData) {
        try {
            const response = await axios.post("asset/checkDuplicates", assetData);
            return response;
        } catch (error) {
            console.error('Error checking duplicates:', error);
            throw error;
        }
    }

    /**
     * Batch duplicate check for bulk imports
     */
    async batchCheckDuplicates(assetsArray) {
        try {
            const response = await axios.post("asset/batchCheckDuplicates", {
                assets: assetsArray
            });
            return response;
        } catch (error) {
            console.error('Error in batch duplicate check:', error);
            throw error;
        }
    }

    /**
     * Resolve duplicate
     */
    async resolveDuplicate(assetId, duplicateId, resolution, notes = '') {
        try {
            const response = await axios.post("asset/resolveDuplicate", {
                asset_id: assetId,
                duplicate_id: duplicateId,
                resolution: resolution,
                notes: notes
            });
            return response;
        } catch (error) {
            console.error('Error resolving duplicate:', error);
            throw error;
        }
    }

    /**
     * Get duplicate detection history
     */
    async getDuplicateHistory() {
        const cacheKey = 'duplicate_history';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("asset/getDuplicateHistory");
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading duplicate history:', error);
            throw error;
        }
    }

    /**
     * Get duplicate statistics
     */
    async getDuplicateStats() {
        const cacheKey = 'duplicate_stats';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("asset/getDuplicateStats");
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading duplicate stats:', error);
            throw error;
        }
    }

    // ==================== BASELINE MANAGEMENT ====================

    /**
     * Get assets that don't have baseline definitions
     */
async getAssetsWithoutBaselines(facilityId = null, asset_cat_id = null) {
        const cacheKey = `assets_without_baselines_${facilityId || 'all'}_${asset_cat_id || 'all'}`;
        const cached = this.getCachedData(cacheKey);
        if (cached) return { data: cached };
 
        try {
            const params = {};
            if (facilityId)    params.facility_id    = facilityId;
            if (asset_cat_id)  params.asset_cat_id   = asset_cat_id;
 
            const response = await axios.get('baseline/getAssetsWithoutBaselines', { params });
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading assets without baselines:', error);
            throw error;
        }
    }
 
    /**
     * Get analytics for assets without baselines
     * ── UPDATED: now accepts asset_cat_id to filter by category ──
     */
    async getAssetsWithoutBaselinesAnalytics(facilityId = null, asset_cat_id = null) {
        const cacheKey = `assets_without_baselines_analytics_${facilityId || 'all'}_${asset_cat_id || 'all'}`;
        const cached = this.getCachedData(cacheKey);
        if (cached) return { data: cached };
 
        try {
            const params = {};
            if (facilityId)    params.facility_id    = facilityId;
            if (asset_cat_id)  params.asset_cat_id   = asset_cat_id;
 
            const response = await axios.get('baseline/getAssetsWithoutBaselinesAnalytics', { params });
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading assets without baselines analytics:', error);
            throw error;
        }
    }
 
    /**
     * Create a new baseline
     */
    async createBaseline(data) {
        try {
            this.clearCache('baseline');
            this.clearCache('compliance');
            this.clearCache('assets_without_baselines');
            
            const response = await axios.post("baseline/createBaseline", data);
            console.log('Baseline created successfully');
            return response;
        } catch (error) {
            console.error('Error creating baseline:', error);
            
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            } else if (error.response?.status === 422) {
                throw new Error('Validation failed: Please check your data and try again');
            } else {
                throw new Error('Failed to create baseline');
            }
        }
    }

    /**
     * Save multiple baseline items in bulk
     */
    async saveBulkBaseline(data) {
        try {
            this.clearCache('baseline');
            this.clearCache('compliance');
            
            this.validateBaselineData(data);
            
            const response = await axios.post("baseline/saveBulkBaseline", data);
            console.log(`Successfully saved ${data.baselines?.length || 0} baseline items`);
            
            return response;
        } catch (error) {
            console.error('Error saving baseline:', error);
            
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            } else if (error.response?.status === 422) {
                throw new Error('Validation failed: Please check your data and try again');
            } else if (error.response?.status === 500) {
                throw new Error('Server error: Please try again later');
            } else {
                throw new Error('Failed to save baseline configuration');
            }
        }
    }

    /**
     * Validate baseline data before saving
     */
    validateBaselineData(data) {
        if (!data || !data.baselines || !Array.isArray(data.baselines)) {
            throw new Error('Invalid baseline data format');
        }

        if (data.baselines.length === 0) {
            throw new Error('No baseline items to save');
        }

        const errors = [];
        const seen = new Set();

        data.baselines.forEach((baseline, index) => {
            const row = index + 1;

            if (!baseline.facility_level) {
                errors.push(`Row ${row}: Facility level is required`);
            }
            if (!baseline.station_point_id) {
                errors.push(`Row ${row}: Station point is required`);
            }
            if (!baseline.item_id) {
                errors.push(`Row ${row}: Item is required`);
            }
            if (!baseline.expected_quantity || baseline.expected_quantity < 1) {
                errors.push(`Row ${row}: Expected quantity must be at least 1`);
            }

            const key = `${baseline.facility_level}_${baseline.station_point_id}_${baseline.item_id}`;
            if (seen.has(key)) {
                errors.push(`Row ${row}: Duplicate baseline entry`);
            }
            seen.add(key);
        });

        if (errors.length > 0) {
            throw new Error(errors.join('\n'));
        }
    }

    /**
     * Get baselines by facility level
     */
    async getBaselinesByLevel(facilityLevel) {
        const cacheKey = `baselines_level_${facilityLevel}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const encodedLevel = encodeURIComponent(facilityLevel);
            const response = await axios.get(`baseline/getBaselinesByLevel/${encodedLevel}`);
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading baselines:', error);
            throw error;
        }
    }

    /**
     * Get compliance status for a specific client
     */
    async getComplianceStatus(clientId, params = {}) {
        const cacheKey = `compliance_${clientId}_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get(`baseline/getComplianceStatus/${clientId}`, { params });
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading compliance status:', error);
            throw error;
        }
    }

    /**
     * Get facility compliance data overview
     */
    async getFacilityComplianceData(params = {}) {
        const cacheKey = `facility_compliance_overview_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("baseline/getFacilityCompliance", { params });
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading facility compliance data:', error);
            throw error;
        }
    }

    /**
     * Get asset gaps (missing/excess items)
     */
    async getAssetGaps(clientId, stationPointId = null, params = {}) {
        const cacheKey = `gaps_${clientId}_${stationPointId || 'all'}_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const endpoint = stationPointId 
                ? `baseline/getAssetGaps/${clientId}/${stationPointId}`
                : `baseline/getAssetGaps/${clientId}`;
            
            const response = await axios.get(endpoint, { params });
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading asset gaps:', error);
            throw error;
        }
    }

    /**
     * Get expected assets for a station
     */
    async getExpectedAssets(clientId, stationPointId, assetCatId, params = {}) {
        const cacheKey = `expected_${clientId}_${stationPointId}_${assetCatId}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get(
                `baseline/getExpectedAssets/${clientId}/${stationPointId}/${assetCatId}`, 
                { params }
            );
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading expected assets:', error);
            throw error;
        }
    }

    /**
     * Get compliance analytics data
     */
    async getComplianceAnalytics(params = {}) {
        const cacheKey = `analytics_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("baseline/getComplianceAnalytics", { params });
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading compliance analytics:', error);
            throw error;
        }
    }

    /**
     * Batch update compliance for multiple clients
     */
    async batchUpdateCompliance(clientIds) {
        try {
            this.clearCache('compliance');
            return await axios.post("baseline/batchUpdateCompliance", { client_ids: clientIds });
        } catch (error) {
            console.error('Error in batch compliance update:', error);
            throw error;
        }
    }

    /**
     * Get batch compliance status for multiple clients
     */
    async getBatchComplianceStatus(clientIds, params = {}) {
        try {
            const response = await axios.post("baseline/getBatchCompliance", { 
                client_ids: clientIds,
                ...params 
            });
            return response;
        } catch (error) {
            console.error('Error getting batch compliance:', error);
            throw error;
        }
    }

    // ==================== BASELINE COMPLIANCE & STANDARDS (CORRECTED FOR YOUR BACKEND) ====================

    /**
     * Get baseline compliance data (uses existing compliance calculation)
     */
    async getBaselineCompliance(params = {}) {
        const cacheKey = `baseline_compliance_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            // Use the facility compliance endpoint
            const response = await axios.get("baseline/getFacilityCompliance", { params });
            
            // Transform the data to match expected format
            const facilities = response.data?.data || response.data || [];
            const complianceData = [];
            
            // For each facility, get detailed compliance
            for (const facility of facilities) {
                if (params.client_id && facility.facility_id !== params.client_id) {
                    continue;
                }
                if (params.site_type && facility.facility_level !== params.site_type) {
                    continue;
                }
                
                try {
                    const detailResponse = await axios.get(`baseline/getComplianceStatus/${facility.facility_id}`);
                    const details = detailResponse.data?.data || [];
                    
                    details.forEach(item => {
                        complianceData.push({
                            ...item,
                            client_id: facility.facility_id,
                            business_name: facility.facility_name,
                            site_type: facility.facility_level,
                            located_region: facility.located_region || 'Unknown'
                        });
                    });
                } catch (err) {
                    console.warn(`Could not load details for facility ${facility.facility_id}`);
                }
            }
            
            this.setCachedData(cacheKey, complianceData);
            return { data: complianceData };
        } catch (error) {
            console.error('Error loading baseline compliance:', error);
            throw error;
        }
    }

    /**
     * Get baseline standards (uses getBaselinesByLevel)
     */
    async getBaselineStandards(params = {}) {
        const cacheKey = `baseline_standards_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            // If facility_level is specified, get baselines for that level
            if (params.facility_level) {
                const response = await axios.get(`baseline/getBaselinesByLevel/${encodeURIComponent(params.facility_level)}`);
                const data = response.data?.data || [];
                this.setCachedData(cacheKey, data);
                return { data };
            }
            
            // Otherwise, get baselines for all levels
            const levels = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5'];
            const allBaselines = [];
            
            for (const level of levels) {
                try {
                    const response = await axios.get(`baseline/getBaselinesByLevel/${encodeURIComponent(level)}`);
                    const levelData = response.data?.data || [];
                    allBaselines.push(...levelData);
                } catch (err) {
                    console.warn(`Could not load baselines for ${level}`);
                }
            }
            
            this.setCachedData(cacheKey, allBaselines);
            return { data: allBaselines };
        } catch (error) {
            console.error('Error loading baseline standards:', error);
            throw error;
        }
    }

    /**
     * Save or update a baseline standard
     */
    async saveBaselineStandard(data) {
        try {
            this.clearCache('baseline');
            this.clearCache('compliance');
            
            // Validate required fields
            if (!data.facility_level) {
                throw new Error('Facility level is required');
            }
            if (!data.station_point_id) {
                throw new Error('Station point is required');
            }
            if (!data.item_id) {
                throw new Error('Item is required');
            }
            if (!data.expected_quantity || data.expected_quantity < 1) {
                throw new Error('Expected quantity must be at least 1');
            }

            // Use saveBulkBaseline for both create and update
            const baselineData = {
                baselines: [{
                    facility_level: data.facility_level,
                    station_point_id: data.station_point_id,
                    item_id: data.item_id,
                    expected_quantity: data.expected_quantity,
                    is_mandatory: data.is_mandatory !== undefined ? data.is_mandatory : 1,
                    notes: data.notes || ''
                }]
            };

            const response = await axios.post("baseline/saveBulkBaseline", baselineData);
            console.log('Baseline standard saved successfully');
            return response;
        } catch (error) {
            console.error('Error saving baseline standard:', error);
            
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            } else if (error.message) {
                throw error;
            } else {
                throw new Error('Failed to save baseline standard');
            }
        }
    }

    /**
     * Delete a baseline standard
     */
    async deleteBaselineStandard(id) {
        try {
            this.clearCache('baseline');
            this.clearCache('compliance');
            
            if (!id) {
                throw new Error('Standard ID is required');
            }

            const response = await axios.post("baseline/delete", { id });
            console.log('Baseline standard deleted successfully');
            return response;
        } catch (error) {
            console.error('Error deleting baseline standard:', error);
            
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            } else if (error.response?.status === 404) {
                throw new Error('Standard not found');
            } else {
                throw new Error('Failed to delete baseline standard');
            }
        }
    }

    /**
     * Calculate compliance for a specific client (uses existing endpoint)
     */
    async calculateCompliance(clientId) {
        try {
            this.clearCache('compliance');
            
            if (!clientId) {
                throw new Error('Client ID is required');
            }

            // Use the getComplianceStatus endpoint which recalculates
            const response = await axios.get(`baseline/getComplianceStatus/${clientId}`);
            console.log('Compliance calculated successfully for client:', clientId);
            return response;
        } catch (error) {
            console.error('Error calculating compliance:', error);
            throw error;
        }
    }

    /**
     * Get compliance summary statistics
     */
    async getComplianceSummary(params = {}) {
        const cacheKey = `compliance_summary_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            // Use facility compliance to generate summary
            const response = await axios.get("baseline/getFacilityCompliance", { params });
            const facilities = response.data?.data || [];
            
            const summary = {
                total_facilities: facilities.length,
                compliant_facilities: facilities.filter(f => parseFloat(f.avg_compliance || 0) >= 100).length,
                partial_compliant: facilities.filter(f => {
                    const comp = parseFloat(f.avg_compliance || 0);
                    return comp >= 50 && comp < 100;
                }).length,
                non_compliant: facilities.filter(f => parseFloat(f.avg_compliance || 0) < 50).length,
                avg_compliance: facilities.length > 0 
                    ? facilities.reduce((sum, f) => sum + parseFloat(f.avg_compliance || 0), 0) / facilities.length 
                    : 0,
                total_missing: facilities.reduce((sum, f) => sum + parseInt(f.missing_items || 0), 0),
                total_excess: facilities.reduce((sum, f) => sum + parseInt(f.excess_items || 0), 0),
                critical_missing: facilities.reduce((sum, f) => sum + parseInt(f.critical_missing || 0), 0)
            };
            
            this.setCachedData(cacheKey, summary);
            return { data: summary };
        } catch (error) {
            console.error('Error loading compliance summary:', error);
            throw error;
        }
    }

    /**
     * Get gap analysis data
     */
    async getGapAnalysis(params = {}) {
        const cacheKey = `gap_analysis_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            // Use facility compliance to find facilities with gaps
            const facilityResponse = await axios.get("baseline/getFacilityCompliance", { params });
            const facilities = facilityResponse.data?.data || [];
            
            // Get detailed gaps for facilities with missing items
            const allGaps = [];
            
            for (const facility of facilities.slice(0, 20)) { // Limit to prevent too many requests
                if (parseInt(facility.missing_items || 0) > 0) {
                    try {
                        const gapResponse = await axios.get(`baseline/getAssetGaps/${facility.facility_id}`);
                        const gaps = gapResponse.data?.data || [];
                        
                        gaps.forEach(gap => {
                            if (gap.status === 'missing') {
                                allGaps.push({
                                    facility_id: facility.facility_id,
                                    facility_name: facility.facility_name,
                                    facility_level: facility.facility_level,
                                    ...gap
                                });
                            }
                        });
                    } catch (err) {
                        console.warn(`Could not load gaps for facility ${facility.facility_id}`);
                    }
                }
            }
            
            // Sort by gap size (descending)
            allGaps.sort((a, b) => Math.abs(b.gap || 0) - Math.abs(a.gap || 0));
            
            this.setCachedData(cacheKey, allGaps.slice(0, 50)); // Return top 50
            return { data: allGaps.slice(0, 50) };
        } catch (error) {
            console.error('Error loading gap analysis:', error);
            throw error;
        }
    }

    /**
     * Bulk update multiple baseline standards
     */
    async bulkUpdateStandards(standards) {
        try {
            this.clearCache('baseline');
            this.clearCache('compliance');
            
            if (!Array.isArray(standards) || standards.length === 0) {
                throw new Error('Standards array is required');
            }

            const response = await axios.post("baseline/saveBulkBaseline", {
                baselines: standards
            });
            
            console.log(`Successfully updated ${standards.length} baseline standards`);
            return response;
        } catch (error) {
            console.error('Error in bulk update:', error);
            throw error;
        }
    }

    /**
     * Import baseline standards from Excel
     */
    async importBaselineStandards(file, facilityLevel) {
        try {
            this.clearCache('baseline');
            this.clearCache('compliance');
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('facility_level', facilityLevel);

            const response = await axios.post("baseline/standards/import", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log('Baseline standards imported successfully');
            return response;
        } catch (error) {
            console.error('Error importing baseline standards:', error);
            throw error;
        }
    }

    /**
     * Export baseline standards to Excel
     */
    async exportBaselineStandards(params = {}) {
        try {
            const response = await axios.get("baseline/standards/export", {
                params,
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `Baseline_Standards_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            return response;
        } catch (error) {
            console.error('Error exporting baseline standards:', error);
            throw error;
        }
    }

    /**
     * Get compliance trend data over time
     */
    async getComplianceTrend(clientId, params = {}) {
        const cacheKey = `compliance_trend_${clientId}_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            // Get current compliance
            const response = await axios.get(`baseline/getComplianceStatus/${clientId}`, { params });
            
            // Note: Backend doesn't have historical data yet
            // Return current data formatted as trend
            const data = response.data?.data || [];
            const trend = [{
                date: new Date().toISOString().split('T')[0],
                compliance_percentage: data.length > 0 
                    ? data.reduce((sum, item) => sum + (item.compliance_percentage || 0), 0) / data.length 
                    : 0
            }];
            
            this.setCachedData(cacheKey, trend);
            return { data: trend };
        } catch (error) {
            console.error('Error loading compliance trend:', error);
            throw error;
        }
    }

    /**
     * Get regional compliance overview
     */
    async getRegionalCompliance(params = {}) {
        const cacheKey = `regional_compliance_${JSON.stringify(params)}`;
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("baseline/getFacilityCompliance", { params });
            const facilities = response.data?.data || [];
            
            // Group by region
            const regionalData = {};
            
            facilities.forEach(facility => {
                const region = facility.located_region || 'Unknown';
                if (!regionalData[region]) {
                    regionalData[region] = {
                        region: region,
                        facilities: 0,
                        avg_compliance: 0,
                        total_missing: 0,
                        total_excess: 0
                    };
                }
                
                regionalData[region].facilities++;
                regionalData[region].avg_compliance += parseFloat(facility.avg_compliance || 0);
                regionalData[region].total_missing += parseInt(facility.missing_items || 0);
                regionalData[region].total_excess += parseInt(facility.excess_items || 0);
            });
            
            // Calculate averages
            Object.values(regionalData).forEach(region => {
                if (region.facilities > 0) {
                    region.avg_compliance = region.avg_compliance / region.facilities;
                }
            });
            
            const result = Object.values(regionalData);
            this.setCachedData(cacheKey, result);
            return { data: result };
        } catch (error) {
            console.error('Error loading regional compliance:', error);
            throw error;
        }
    }

    // ==================== REPORTING & ANALYTICS ====================
    
    /**
     * Get asset inventory report data
     */
    getAssetInventoryReportData(params) {
        return axios.post("getAssetInventoryReportData", params);
    }

    /**
     * Get totals by asset category
     */
    getTotalsByAssetCategory() {
        const cacheKey = 'totals_by_category';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return Promise.resolve({ data: cached });
        }

        return axios.get("getTotalsByAssetCategory").then(response => {
            this.setCachedData(cacheKey, response.data);
            return response;
        });
    }

    /**
     * Get totals by functional status
     */
    getTotalByAssetFunctionalStatus() {
        const cacheKey = 'totals_by_status';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return Promise.resolve({ data: cached });
        }

        return axios.get("getTotalByAssetFunctionalStatus").then(response => {
            this.setCachedData(cacheKey, response.data);
            return response;
        });
    }

    // ==================== DATABASE SCHEMA & CUSTOM REPORTS ====================

    /**
     * Get database schema information
     */
    async getDatabaseSchema() {
        const cacheKey = 'database_schema';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("database/schema");
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading database schema:', error);
            throw error;
        }
    }

    /**
     * Execute custom report
     */
    async executeReport(config) {
        try {
            const response = await axios.post("database/executeReport", config);
            return response;
        } catch (error) {
            console.error('Error executing report:', error);
            throw error;
        }
    }

    /**
     * Save custom report configuration
     */
    async saveReport(reportData) {
        try {
            const response = await axios.post("database/saveReport", reportData);
            this.clearCache('saved_reports');
            return response;
        } catch (error) {
            console.error('Error saving report:', error);
            throw error;
        }
    }

    /**
     * Get saved reports
     */
    async getSavedReports() {
        const cacheKey = 'saved_reports';
        const cached = this.getCachedData(cacheKey);
        
        if (cached) {
            return { data: cached };
        }

        try {
            const response = await axios.get("database/savedReports");
            this.setCachedData(cacheKey, response.data);
            return response;
        } catch (error) {
            console.error('Error loading saved reports:', error);
            throw error;
        }
    }

    /**
     * Export report to Excel
     */
    async exportToExcel(config) {
        try {
            const response = await axios.post("database/exportExcel", config, {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${config.reportName || 'report'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            return response;
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            throw error;
        }
    }
 // CORRECT — class method shorthand syntax
async verifyVehicle(data) {
    const response = await axios.post('asset/verifyVehicle', data);
    return response.data;
}

async markVehicleNotFound(assetId, visitId, description) {
    const response = await axios.post('asset/markVehicleNotFound', {
        id: assetId,
        visit_id: visitId,
        description: description || 'Vehicle could not be located during ground verification',
    });
    return response.data;
}

async getVehicleVerificationProgress(workPlanId = null) {
    const url = workPlanId
        ? `asset/vehicleVerificationProgress?work_plan_id=${workPlanId}`
        : 'asset/vehicleVerificationProgress';
    const response = await axios.get(url);
    return response.data;
}
}

// Export singleton instance
export default new AssetService();